import type { BenefitSummary, BenefitTier } from "@yutai/shared"

// Matches a yen amount (e.g. "3,000円" or "3,000円分"), optionally followed
// by a closing bracket and a quantity count that multiplies it. Mirrors
// apps/worker/src/infra/tierValueExtractor.ts — duplicated for client-side
// display before benefits.json is republished with benefitValue fields.
const YEN_AMOUNT_PATTERN = /([\d,]+)\s*円(?:分)?[)）]?\s*(\d+)?\s*(?:枚|個|口|冊|本|セット|点)?/g

function extractYenValueFromText(text: string): number {
  let total = 0
  for (const match of text.matchAll(YEN_AMOUNT_PATTERN)) {
    const amount = Number(match[1].replace(/,/g, ""))
    if (!Number.isFinite(amount)) continue
    const quantity = match[2] ? Number(match[2]) : 1
    total += amount * quantity
  }
  return total
}

export interface HoldingBenefitAmount {
  amount: number | null
  isEstimated: boolean
}

// Highest tier whose minimumShares the held share count qualifies for.
export function getApplicableTier(tiers: BenefitTier[], shares: number): BenefitTier | null {
  let applicable: BenefitTier | null = null
  for (const tier of [...tiers].sort((a, b) => a.minimumShares - b.minimumShares)) {
    if (tier.minimumShares <= shares) applicable = tier
  }
  return applicable
}

function resolveTierBenefitValue(tier: BenefitTier): number | null {
  if (tier.benefitValue != null && tier.benefitValue > 0) return tier.benefitValue
  const fromText = extractYenValueFromText(tier.description)
  return fromText > 0 ? fromText : null
}

// 保有株数に対応する tier の benefitValue を優先し、未収集なら minimumBenefitValue にフォールバック。
export function getHoldingBenefitAmount(
  benefit: Pick<BenefitSummary, "minimumBenefitValue" | "minimumShares" | "tiers">,
  shares: number
): HoldingBenefitAmount {
  if (!Number.isFinite(shares) || shares <= 0) {
    return { amount: null, isEstimated: false }
  }

  const tier = getApplicableTier(benefit.tiers, shares)
  if (tier) {
    const tierValue = resolveTierBenefitValue(tier)
    if (tierValue != null) {
      return { amount: tierValue, isEstimated: false }
    }
    if (benefit.minimumBenefitValue != null && benefit.minimumBenefitValue > 0) {
      return { amount: benefit.minimumBenefitValue, isEstimated: true }
    }
    return { amount: null, isEstimated: false }
  }

  if (
    benefit.minimumShares <= shares &&
    benefit.minimumBenefitValue != null &&
    benefit.minimumBenefitValue > 0
  ) {
    return { amount: benefit.minimumBenefitValue, isEstimated: false }
  }

  return { amount: null, isEstimated: false }
}

export interface HoldingYield {
  actualDividendYield: number | null // %、取得単価に対する実際の配当利回り
  actualBenefitYield: number | null // %、取得単価×株数に対する実際の優待利回り
}

export interface ListItemYields {
  dividendYield: number | null
  benefitYield: number | null
  combinedYield: number | null
  investment: number | null
  dividendIncome: number | null
  benefitIncome: number | null
  benefitAmount: number | null
  benefitAmountEstimated: boolean
}

export interface AggregatedListYields {
  dividendYield: number | null
  benefitYield: number | null
  combinedYield: number | null
  totalInvestment: number | null
  totalShares: number | null
  totalBenefitAmount: number | null
}

export function combinedYieldValues(
  dividendYield: number | null,
  benefitYield: number | null
): number | null {
  if (dividendYield == null || benefitYield == null) return null
  return dividendYield + benefitYield
}

// benefits.json公開データ(概算株価ベースのdividendYield)から、ユーザーの
// 実際の取得単価に対する利回りを逆算する。dividendPerShareは生の値として
// 公開されていないため、approxStockPrice × dividendYield%から復元する。
export function computeHoldingYield(
  benefit: Pick<
    BenefitSummary,
    "dividendYield" | "approxStockPrice" | "tiers" | "minimumBenefitValue" | "minimumShares"
  >,
  acquisitionPrice: number,
  acquisitionShares: number
): HoldingYield {
  // dividendPerShare(円) = dividendYield% × approxStockPrice ÷ 100 を経由して
  // 実際の取得単価に対する利回りに換算する（%どうしの単純な比の変換なので
  // 分子分母の100は相殺され、結果は dividendYield × approxStockPrice ÷ 取得単価）。
  const actualDividendYield =
    benefit.dividendYield != null && benefit.approxStockPrice != null && acquisitionPrice > 0
      ? (benefit.dividendYield * benefit.approxStockPrice) / acquisitionPrice
      : null

  const { amount: benefitAmount } = getHoldingBenefitAmount(benefit, acquisitionShares)
  const actualBenefitYield =
    benefitAmount != null && acquisitionPrice > 0 && acquisitionShares > 0
      ? (benefitAmount / (acquisitionPrice * acquisitionShares)) * 100
      : null

  return { actualDividendYield, actualBenefitYield }
}

export function getListItemYields(
  item: { acquisitionPrice: number | null; acquisitionShares: number | null },
  benefit:
    | Pick<
        BenefitSummary,
        | "dividendYield"
        | "benefitYield"
        | "approxStockPrice"
        | "tiers"
        | "minimumBenefitValue"
        | "minimumShares"
      >
    | undefined,
  isHoldingMode: boolean
): ListItemYields {
  const price = item.acquisitionPrice
  const shares = item.acquisitionShares
  const hasShares = shares != null && shares > 0
  const hasHolding = price != null && hasShares && price > 0

  if (isHoldingMode && benefit) {
    const benefitAmountResult = hasShares
      ? getHoldingBenefitAmount(benefit, shares)
      : { amount: null, isEstimated: false }

    if (hasHolding) {
      const { actualDividendYield, actualBenefitYield } = computeHoldingYield(
        benefit,
        price,
        shares
      )
      const investment = price * shares
      const dividendIncome =
        actualDividendYield != null ? (actualDividendYield * investment) / 100 : null
      const benefitIncome =
        actualBenefitYield != null ? (actualBenefitYield * investment) / 100 : null
      return {
        dividendYield: actualDividendYield,
        benefitYield: actualBenefitYield,
        combinedYield: combinedYieldValues(actualDividendYield, actualBenefitYield),
        investment,
        dividendIncome,
        benefitIncome,
        benefitAmount: benefitAmountResult.amount,
        benefitAmountEstimated: benefitAmountResult.isEstimated,
      }
    }

    return {
      dividendYield: benefit.dividendYield,
      benefitYield: benefit.benefitYield,
      combinedYield: combinedYieldValues(benefit.dividendYield, benefit.benefitYield),
      investment: null,
      dividendIncome: null,
      benefitIncome: null,
      benefitAmount: benefitAmountResult.amount,
      benefitAmountEstimated: benefitAmountResult.isEstimated,
    }
  }

  if (benefit) {
    return {
      dividendYield: benefit.dividendYield,
      benefitYield: benefit.benefitYield,
      combinedYield: combinedYieldValues(benefit.dividendYield, benefit.benefitYield),
      investment: hasHolding ? price * shares : null,
      dividendIncome: null,
      benefitIncome: null,
      benefitAmount: null,
      benefitAmountEstimated: false,
    }
  }

  return {
    dividendYield: null,
    benefitYield: null,
    combinedYield: null,
    investment: null,
    dividendIncome: null,
    benefitIncome: null,
    benefitAmount: null,
    benefitAmountEstimated: false,
  }
}

export function aggregateListYields(
  rows: ListItemYields[],
  shareCounts: number[]
): AggregatedListYields {
  let totalInvestment = 0
  let totalDividendIncome = 0
  let totalBenefitIncome = 0
  let totalBenefitAmount = 0
  let hasBenefitAmount = false
  let totalShares = 0
  let hasWeighted = false

  let divSum = 0
  let divCount = 0
  let benSum = 0
  let benCount = 0
  let combSum = 0
  let combCount = 0

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const shares = shareCounts[i]

    if (shares > 0) totalShares += shares

    if (row.benefitAmount != null) {
      hasBenefitAmount = true
      totalBenefitAmount += row.benefitAmount
    }

    if (row.investment != null && row.investment > 0) {
      if (row.dividendIncome != null || row.benefitIncome != null) {
        hasWeighted = true
        totalInvestment += row.investment
        if (row.dividendIncome != null) totalDividendIncome += row.dividendIncome
        if (row.benefitIncome != null) totalBenefitIncome += row.benefitIncome
      }
    }

    if (row.dividendYield != null) {
      divSum += row.dividendYield
      divCount++
    }
    if (row.benefitYield != null) {
      benSum += row.benefitYield
      benCount++
    }
    if (row.combinedYield != null) {
      combSum += row.combinedYield
      combCount++
    }
  }

  if (hasWeighted && totalInvestment > 0) {
    const dividendYield =
      totalDividendIncome > 0 ? (totalDividendIncome / totalInvestment) * 100 : null
    const benefitYield =
      totalBenefitIncome > 0 ? (totalBenefitIncome / totalInvestment) * 100 : null
    const combinedYield =
      dividendYield != null && benefitYield != null ? dividendYield + benefitYield : null
    return {
      dividendYield,
      benefitYield,
      combinedYield,
      totalInvestment,
      totalShares: totalShares > 0 ? totalShares : null,
      totalBenefitAmount: hasBenefitAmount ? totalBenefitAmount : null,
    }
  }

  return {
    dividendYield: divCount > 0 ? divSum / divCount : null,
    benefitYield: benCount > 0 ? benSum / benCount : null,
    combinedYield: combCount > 0 ? combSum / combCount : null,
    totalInvestment: totalInvestment > 0 ? totalInvestment : null,
    totalShares: totalShares > 0 ? totalShares : null,
    totalBenefitAmount: hasBenefitAmount ? totalBenefitAmount : null,
  }
}

export function formatYieldPercent(value: number | null): string {
  return value != null ? `${value.toFixed(1)}%` : "—"
}

// "2024-03-15" -> "1年4ヶ月" のような経過保有期間表示。
export function formatElapsedHoldingPeriod(
  acquisitionDate: string,
  now: Date = new Date()
): string {
  const start = new Date(acquisitionDate)
  if (Number.isNaN(start.getTime())) return ""

  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  if (months < 0) return "0ヶ月"

  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  if (years === 0) return `${remainingMonths}ヶ月`
  if (remainingMonths === 0) return `${years}年`
  return `${years}年${remainingMonths}ヶ月`
}
